import React, { useState, useContext } from 'react';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Question, EnvelopeSimple, PaperPlaneTilt, CheckCircle, ChatText, Lightbulb, Bug, Star } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'suggestion', label: 'Feature Suggestion', icon: Lightbulb },
  { value: 'bug', label: 'Bug Report', icon: Bug },
  { value: 'general', label: 'General Feedback', icon: ChatText },
  { value: 'wishlist', label: 'Wish List', icon: Star },
];

const HelpButton = () => {
  const { user } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('contact');
  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState('suggestion');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const resetForm = () => {
    setSubject('');
    setCategory('suggestion');
    setMessage('');
    setSent(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/feedback`, { subject: subject.trim(), category, message: message.trim() });
      setSent(true);
      toast.success('Feedback submitted! Thank you.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit feedback');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        data-testid="help-button"
        onClick={() => { setOpen(true); setTab('contact'); resetForm(); }}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
        style={{
          background: 'rgba(201,206,211,0.08)',
          border: '1px solid rgba(201,206,211,0.15)',
          color: 'var(--gold)',
        }}
      >
        <Question size={15} weight="bold" />
        Help
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" style={{ background: 'var(--bg-panel)', border: '1px solid var(--border-primary)', color: 'var(--text-primary)' }}>
          <DialogHeader>
            <DialogTitle className="font-heading text-xl" style={{ color: 'var(--gold-light)' }}>
              Help & Feedback
            </DialogTitle>
          </DialogHeader>

          {/* Tabs */}
          <div className="flex rounded-lg overflow-hidden mb-5" style={{ background: 'var(--bg-deep)', border: '1px solid var(--border-subtle)' }}>
            <button
              data-testid="help-tab-contact"
              onClick={() => { setTab('contact'); resetForm(); }}
              className="flex-1 py-2.5 text-sm font-semibold transition-all"
              style={{
                background: tab === 'contact' ? 'rgba(201,206,211,0.12)' : 'transparent',
                color: tab === 'contact' ? 'var(--gold-light)' : 'var(--text-muted)',
              }}
            >
              Contact Us
            </button>
            <button
              data-testid="help-tab-feedback"
              onClick={() => { setTab('feedback'); setSent(false); }}
              className="flex-1 py-2.5 text-sm font-semibold transition-all"
              style={{
                background: tab === 'feedback' ? 'rgba(201,206,211,0.12)' : 'transparent',
                color: tab === 'feedback' ? 'var(--gold-light)' : 'var(--text-muted)',
              }}
            >
              Send Feedback
            </button>
          </div>

          {/* Contact Us Tab */}
          {tab === 'contact' && (
            <div className="space-y-5" data-testid="help-contact-section">
              <div className="hb-card p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(201,206,211,0.1)' }}>
                    <EnvelopeSimple size={22} weight="duotone" style={{ color: 'var(--gold)' }} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Email Us</p>
                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>We typically respond within 24 hours</p>
                  </div>
                </div>
                <a
                  href="mailto:Admin@Harberbids.com"
                  data-testid="contact-email-link"
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all"
                  style={{ background: 'rgba(201,206,211,0.08)', border: '1px solid rgba(201,206,211,0.2)', color: 'var(--gold-light)', textDecoration: 'none' }}
                >
                  <EnvelopeSimple size={16} weight="bold" />
                  Admin@Harberbids.com
                </a>
              </div>
              <div className="text-center">
                <p className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>
                  Have a suggestion or feature request?
                </p>
                <Button
                  data-testid="switch-to-feedback-btn"
                  onClick={() => setTab('feedback')}
                  className="btn-gold gap-2 text-sm"
                  size="sm"
                >
                  <ChatText size={15} weight="bold" />
                  Send Feedback
                </Button>
              </div>
            </div>
          )}

          {/* Feedback Tab */}
          {tab === 'feedback' && (
            <div data-testid="help-feedback-section">
              {sent ? (
                <div className="text-center py-8" data-testid="feedback-success">
                  <CheckCircle size={48} weight="duotone" className="mx-auto mb-3" style={{ color: '#4ade80' }} />
                  <h3 className="text-lg font-semibold font-heading mb-1" style={{ color: 'var(--text-primary)' }}>Thank You!</h3>
                  <p className="text-sm mb-5" style={{ color: 'var(--text-secondary)' }}>Your feedback has been submitted. We appreciate your input.</p>
                  <div className="flex gap-3 justify-center">
                    <Button onClick={() => { resetForm(); }} size="sm" className="btn-ghost text-sm">Send Another</Button>
                    <Button onClick={() => setOpen(false)} size="sm" className="btn-gold text-sm">Done</Button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Pre-filled info */}
                  <div className="flex items-center gap-3 px-3 py-2 rounded-lg" style={{ background: 'rgba(201,206,211,0.05)', border: '1px solid var(--border-subtle)' }}>
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold" style={{ background: 'rgba(201,206,211,0.15)', color: 'var(--gold)' }}>
                      {user?.name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div>
                      <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{user?.name}</p>
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{user?.email} &middot; {user?.role}</p>
                    </div>
                  </div>

                  {/* Category */}
                  <div>
                    <Label className="text-xs mb-2 block" style={{ color: 'var(--text-secondary)' }}>Category</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {CATEGORIES.map(cat => {
                        const Icon = cat.icon;
                        const active = category === cat.value;
                        return (
                          <button
                            key={cat.value}
                            type="button"
                            data-testid={`feedback-category-${cat.value}`}
                            onClick={() => setCategory(cat.value)}
                            className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition-all"
                            style={{
                              background: active ? 'rgba(201,206,211,0.12)' : 'var(--bg-elevated)',
                              border: `1px solid ${active ? 'var(--gold)' : 'var(--border-subtle)'}`,
                              color: active ? 'var(--gold-light)' : 'var(--text-muted)',
                            }}
                          >
                            <Icon size={14} weight={active ? 'fill' : 'regular'} />
                            {cat.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Subject */}
                  <div>
                    <Label htmlFor="feedback-subject" className="text-xs mb-1 block" style={{ color: 'var(--text-secondary)' }}>Subject</Label>
                    <Input
                      id="feedback-subject"
                      data-testid="feedback-subject"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      placeholder="Brief summary..."
                      required
                      className="hb-input"
                    />
                  </div>

                  {/* Message */}
                  <div>
                    <Label htmlFor="feedback-message" className="text-xs mb-1 block" style={{ color: 'var(--text-secondary)' }}>Your Feedback</Label>
                    <Textarea
                      id="feedback-message"
                      data-testid="feedback-message"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Share your suggestion, report a bug, or tell us what you'd like to see..."
                      rows={4}
                      required
                      className="hb-input"
                    />
                  </div>

                  {/* Submit */}
                  <Button
                    type="submit"
                    data-testid="feedback-submit"
                    disabled={sending || !subject.trim() || !message.trim()}
                    className="w-full btn-gold gap-2"
                  >
                    <PaperPlaneTilt size={16} weight="bold" />
                    {sending ? 'Sending...' : 'Submit Feedback'}
                  </Button>
                </form>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default HelpButton;
